import React from 'react';
import { useSelector } from 'react-redux';
import type Page from 'models/IObject';
import NavigationButton from './NavigationButton';
import useFocusArea from 'hooks/use-focus-area';
import useLegacyCollection from 'hooks/use-legacy-collection';
import usePagesV3Collection from 'hooks/use-pages-v3-collection';
import { getRefreshKey } from 'services/admin/selectors';
import PageNavigationContext, { usePageNavigationContext } from './Context';
import CreationOptions from './CreationOptions';
import PageSearch from './PageSearch';
import SiteNavigation from '../SiteNavigation';
import { PageNavigationWrapper, PageNavigationMenuLegacy, SiteNavigationExpandableWrapper } from './styles';
import { getIsNavigationV1Enabled, getIsPagesV3Enabled } from 'services/feature-gate/selectors';

const PageNavigation = () => {
  const refreshKey = useSelector(getRefreshKey);
  const isNavigationV1Enabled = useSelector(getIsNavigationV1Enabled);
  const isPagesV3Enabled = useSelector(getIsPagesV3Enabled);
  const { closeNavigation, openNavigation, isOpen, searchText } = usePageNavigationContext();

  const pagesHook = isPagesV3Enabled ? usePagesV3Collection : useLegacyCollection;

  const [firstPage] = pagesHook<Page>({
    collection: 'pages',
    limit: 50,
    pageOffset: 0,
    refreshKey,
    search: '',
  });

  const pageNavRef = useFocusArea<HTMLDivElement>({
    onExit: closeNavigation,
    active: isOpen,
  });

  const toggleNavigation = () => {
    if (isOpen) {
      closeNavigation();
    } else {
      openNavigation();
    }
  };

  const renderNavigation = () => {
    if (isNavigationV1Enabled) {
      return (
        <SiteNavigationExpandableWrapper
          isExpanded={isOpen}
          fixedExpandedHeight="calc(100vh - 51px)"
        >
          <SiteNavigation />
        </SiteNavigationExpandableWrapper>
      );
    }

    // TODO-navigation-v1 - delete this after navigation v1
    return (
      <PageNavigationMenuLegacy
        isExpanded={isOpen}
        minHeight={searchText ? 0 : Math.min(firstPage.length * 52, 312) + 215}
      >
        <CreationOptions onPageCreation={closeNavigation} />
        <PageSearch firstPage={firstPage} />
      </PageNavigationMenuLegacy>
    );
  };

  return (
    <PageNavigationWrapper ref={pageNavRef}>
      <NavigationButton isActive={isOpen} onClick={toggleNavigation} />
      {renderNavigation()}
    </PageNavigationWrapper>
  );
};

export default () => {
  return (
    <PageNavigationContext>
      <PageNavigation />
    </PageNavigationContext>
  );
};
